// components/video/SignalErrorBoundary.tsx
import FeatherIcon from 'feather-icons-react';
import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button } from 'react-bootstrap';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
  lastErrorTime: number;
}

class SignalErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorCount: 0,
      lastErrorTime: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const now = Date.now();
    return {
      hasError: true,
      error,
      lastErrorTime: now
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log do erro
    console.error('🛡️ SignalErrorBoundary capturou erro:', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      errorBoundary: 'SignalErrorBoundary'
    });

    // Callback opcional
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Incrementar contador de erros
    this.setState(prevState => ({
      errorCount: prevState.errorCount + 1
    }));

    // Auto-recuperação após 3 segundos para erros de DOM
    if (
      error.message.includes('removeChild') ||
      error.message.includes('appendChild') ||
      error.message.includes('insertBefore')
    ) {
      console.log('🔄 Tentando auto-recuperação em 3 segundos...');

      if (this.resetTimeoutId) {
        clearTimeout(this.resetTimeoutId);
      }

      this.resetTimeoutId = setTimeout(() => {
        this.resetError();
      }, 3000);
    }

    // Se houver muitos erros em sequência, tentar reset mais agressivo
    const { lastErrorTime, errorCount } = this.state;
    const timeSinceLastError = Date.now() - lastErrorTime;

    if (errorCount > 3 && timeSinceLastError < 5000) {
      console.warn('⚠️ Muitos erros em sequência, forçando reset completo...');
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetError = () => {
    console.log('🔄 Resetando estado de erro...');
    this.setState({
      hasError: false,
      error: null
    });

    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  };

  render() {
    if (this.state.hasError) {
      // Se um fallback customizado foi fornecido
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Verificar tipo de erro
      const isDOMError =
        this.state.error?.message.includes('removeChild') ||
        this.state.error?.message.includes('appendChild') ||
        this.state.error?.message.includes('insertBefore');

      // UI de erro padrão
      return (
        <Alert variant={isDOMError ? 'warning' : 'danger'} className="m-3">
          <Alert.Heading>
            <FeatherIcon icon="alert-triangle" size={20} className="me-2" />
            {isDOMError ? 'Erro temporário detectado' : 'Oops! Algo deu errado'}
          </Alert.Heading>

          {isDOMError ? (
            <>
              <p>
                Um erro de sincronização do DOM foi detectado. Isso geralmente
                acontece durante salvamentos rápidos.
              </p>
              <div className="d-flex align-items-center gap-2">
                <div
                  className="spinner-border spinner-border-sm text-warning"
                  role="status"
                >
                  <span className="visually-hidden">Recuperando...</span>
                </div>
                <small className="text-muted">
                  Recuperando automaticamente em alguns segundos...
                </small>
              </div>
            </>
          ) : (
            <>
              <p>Ocorreu um erro inesperado ao processar a sinalização.</p>
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-2">
                  <summary className="text-muted" style={{ cursor: 'pointer' }}>
                    Detalhes técnicos
                  </summary>
                  <pre
                    className="mt-2 p-2 bg-light rounded"
                    style={{ fontSize: '12px' }}
                  >
                    {this.state.error?.message}
                    {'\n'}
                    {this.state.error?.stack}
                  </pre>
                </details>
              )}
            </>
          )}

          <hr />

          <div className="d-flex gap-2">
            <Button variant="primary" size="sm" onClick={this.resetError}>
              <FeatherIcon icon="refresh-cw" size={14} className="me-1" />
              Tentar novamente
            </Button>

            {!isDOMError && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => window.location.reload()}
              >
                <FeatherIcon icon="rotate-cw" size={14} className="me-1" />
                Recarregar página
              </Button>
            )}
          </div>

          {this.state.errorCount > 2 && (
            <Alert variant="info" className="mt-3 mb-0">
              <small>
                <strong>Dica:</strong> Se o erro persistir, tente salvar suas
                alterações e recarregar a página.
              </small>
            </Alert>
          )}
        </Alert>
      );
    }

    return this.props.children;
  }
}

export default SignalErrorBoundary;
